import React from "react";
import ReactSelect, {
  MultiValue,
  SingleValue,
  StylesConfig,
} from "react-select";
import type { CSSObjectWithLabel } from "react-select";
import { Loader2 } from "lucide-react";

import { SELECT_STYLES } from "../../../components/DateFilters/constants";
import { useRelatorioSabores } from "../context/RelatorioSaboresContext";
import RelatorioPeriodoMaxHint from "./RelatorioPeriodoMaxHint";

type Opt = { value: number; label: string };

const SABOR_MULTI_STYLES: StylesConfig<Opt, true> = {
  ...(SELECT_STYLES as unknown as StylesConfig<Opt, true>),
  valueContainer: (base: CSSObjectWithLabel) => ({
    ...base,
    maxHeight: "6.5rem",
    overflowY: "auto",
  }),
};

const RelatorioSaboresFiltersBlock: React.FC = () => {
  const {
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    selectedLojaId,
    setSelectedLojaId,
    selectedTamanhoId,
    setSelectedTamanhoId,
    selectedSaborIds,
    setSelectedSaborIds,
    selectedUsuarioId,
    setSelectedUsuarioId,
    handleApplyFilter,
    handleClearOptionalFilters,
    lojaOptions,
    tamanhoOptions,
    saborOptions,
    usuarioOptions,
    isLoading,
  } = useRelatorioSabores();

  const lojaSel = lojaOptions.find((o) => o.value === selectedLojaId) ?? null;
  const tamanhoSel =
    tamanhoOptions.find((o) => o.value === selectedTamanhoId) ?? null;
  const saborSel = saborOptions.filter((o) =>
    selectedSaborIds.includes(o.value)
  );
  const usuarioSel =
    usuarioOptions.find((o) => o.value === selectedUsuarioId) ?? null;

  return (
    <div className="rounded-lg border border-neutral-200 bg-neutral-50/80 p-4">
      <p className="mb-3 text-sm font-medium text-neutral-800">Filtros</p>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
        <div className="flex flex-col gap-1">
          <label
            htmlFor="rel-sab-ini"
            className="text-xs font-medium text-neutral-700"
          >
            Data início
          </label>
          <input
            id="rel-sab-ini"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-primary"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label
            htmlFor="rel-sab-fim"
            className="text-xs font-medium text-neutral-700"
          >
            Data fim
          </label>
          <input
            id="rel-sab-fim"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-primary"
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-neutral-700">Loja</span>
          <ReactSelect<Opt, false>
            inputId="rel-sab-loja"
            value={lojaSel}
            onChange={(o: SingleValue<Opt>) =>
              setSelectedLojaId(o?.value ?? null)
            }
            options={lojaOptions}
            isClearable
            placeholder="Todas"
            styles={SELECT_STYLES}
            className="text-sm"
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-neutral-700">Tamanho</span>
          <ReactSelect<Opt, false>
            inputId="rel-sab-tam"
            value={tamanhoSel}
            onChange={(o: SingleValue<Opt>) =>
              setSelectedTamanhoId(o?.value ?? null)
            }
            options={tamanhoOptions}
            isClearable
            placeholder="Todos"
            styles={SELECT_STYLES}
            className="text-sm"
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-neutral-700">Sabores</span>
          <ReactSelect<Opt, true>
            isMulti
            closeMenuOnSelect={false}
            inputId="rel-sab-sabor"
            value={saborSel}
            onChange={(opts: MultiValue<Opt>) =>
              setSelectedSaborIds(opts.map((o) => o.value))
            }
            options={saborOptions}
            isClearable
            placeholder="Selecione um ou mais sabores"
            noOptionsMessage={() => "Nenhum sabor encontrado"}
            styles={SABOR_MULTI_STYLES}
            className="text-sm"
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-neutral-700">
            Pizzaiolo
          </span>
          <ReactSelect<Opt, false>
            inputId="rel-sab-user"
            value={usuarioSel}
            onChange={(o: SingleValue<Opt>) =>
              setSelectedUsuarioId(o?.value ?? null)
            }
            options={usuarioOptions}
            isClearable
            placeholder="Todos"
            styles={SELECT_STYLES}
            className="text-sm"
          />
        </div>
      </div>
      <RelatorioPeriodoMaxHint />
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={handleApplyFilter}
          disabled={!startDate || !endDate || isLoading}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
          Aplicar filtros
        </button>
        <button
          type="button"
          onClick={handleClearOptionalFilters}
          className="rounded-lg border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
        >
          Limpar Filtros
        </button>
      </div>
    </div>
  );
};

export default RelatorioSaboresFiltersBlock;
